<template>
  <v-form
    ref="form"
    v-model="valid"
    lazy-validation
    @submit.prevent="submit"
    @keypress.enter="submit"
  >
    <p class="text-justify mb-5">
      <template v-if="selectedMethod.name === 'SMS'">
        Uma mensagem de texto com um código de verificação foi enviada para {{ selectedMethod.value }}. Insira o código
        no campo abaixo para avançar. Este código é válido por 15 minutos!
      </template>
      <template v-else>
        Um código de verificação foi enviado para o seu e-mail. Insira o código no campo abaixo para avançar. Este código
        é válido por 15 minutos!
      </template>
    </p>

    <v-otp-input
      v-model="code"
      min-width="100%"
      base-color="primary"
      color="white"
      required
      length="6"
      :rules="rules.code"
    />

    <v-row>
      <v-col>
        <t-btn
          block
          color="grey"
          class="heading--text"
          large
          :disabled="loading"
          @click="$emit('return')"
        >
          Voltar
        </t-btn>
      </v-col>
      <v-col>
        <t-btn
          block
          color="success"
          type="submit"
          class="heading--text"
          large
          :loading="loading"
          :disabled="!valid"
        >
          Avançar
        </t-btn>
      </v-col>
    </v-row>
  </v-form>
</template>

<script setup lang="ts">
import { ref, computed, useTemplateRef, type Ref } from "vue";
import type User from "@/types/User";
import TBtn from "@/components/core/TBtn.vue";
import axios from "axios";
import getPasswordRecoveryToken, { type PasswordToken } from "@/api/requests/getPasswordRecoveryToken";
import type RecoveryMethod from "@/types/RecoveryMethod";
import { useSnackbar } from "@/stores/snackbar";

interface Props {
  modelValue?: User;
  selectedMethod: RecoveryMethod;
}

const {
  modelValue = {} as User,
} = defineProps<Props>();

const emit = defineEmits<{
  "return": [];
  "submit": [token: PasswordToken];
  "update:modelValue": [user: User];
}>();

defineOptions({
  name: "FormValidateRecoverCode",
});

const snackbar = useSnackbar();
const form = useTemplateRef<{ validate: () => boolean }>("form");

const valid: Ref<boolean> = ref(true);
const loading: Ref<boolean> = ref(false);
const code: Ref<string> = ref("");

const rules = {
  code: [ (v: string) => !!v || "O código é obrigatório" ],
};

const user = computed<User>(() => modelValue);

async function submit(): Promise<void> {
  try {
    if (form.value?.validate()) {
      loading.value = true;

      const token = await getPasswordRecoveryToken(user.value.document, code.value);
      emit("submit", token);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      snackbar.show("Código não encontrado", "error");
    }
  } finally {
    loading.value = false;
  }
}
</script>
